import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus } from 'lucide-react';
import { useState } from 'react';
import { type ISector, sectorApi } from '@/api/commercial/sectors';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';

interface SectorManagementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SectorManagementDialog({ open, onOpenChange }: SectorManagementDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [newSectorName, setNewSectorName] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['sectors'],
    queryFn: () => sectorApi.findMany({ limit: 100 }),
    enabled: open,
  });

  const createMutation = useMutation({
    mutationFn: (name: string) => sectorApi.create(name),
    onSuccess: (result) => {
      if (result.success) {
        toast({ title: 'Setor criado com sucesso', variant: 'success' });
        setNewSectorName('');
        queryClient.invalidateQueries({ queryKey: ['sectors'] });
      } else {
        toast({
          title: result.error?.global || 'Erro ao criar setor',
          variant: 'destructive',
        });
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: { name?: string; enabled?: boolean } }) =>
      sectorApi.update(id, payload),
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['sectors'] });
      } else {
        toast({
          title: result.error?.global || 'Erro ao atualizar setor',
          variant: 'destructive',
        });
      }
    },
  });

  const handleCreate = () => {
    const trimmed = newSectorName.trim();
    if (!trimmed) return;
    createMutation.mutate(trimmed);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCreate();
    }
  };

  const handleToggleEnabled = (sector: ISector) => {
    updateMutation.mutate({
      id: sector.id,
      payload: { enabled: !sector.enabled },
    });
  };

  const sectors = data?.docs || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Gerenciar Setores</DialogTitle>
        </DialogHeader>

        {/* Add new sector */}
        <div className="flex gap-2">
          <Input
            placeholder="Nome do setor"
            value={newSectorName}
            onChange={(e) => setNewSectorName(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={createMutation.isPending}
          />
          <Button
            onClick={handleCreate}
            disabled={!newSectorName.trim() || createMutation.isPending}
            className="shrink-0"
          >
            {createMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4 mr-1" />
            )}
            Adicionar
          </Button>
        </div>

        {/* Sectors table */}
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : sectors.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">Nenhum setor cadastrado</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead className="w-[100px] text-center">Habilitado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sectors.map((sector) => (
                <TableRow key={sector.id}>
                  <TableCell>{sector.name}</TableCell>
                  <TableCell className="text-center">
                    <Switch
                      checked={sector.enabled}
                      onCheckedChange={() => handleToggleEnabled(sector)}
                      disabled={updateMutation.isPending}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
}
